/**
 * Content Credentials (C2PA) - data normalizer.
 *
 * Decast recordings come in two shapes:
 *   A) legacy BBB - the serve API attaches `recording.provenance`
 *      ({ signed, issuer, signed_at, algorithm, manifest_label, ai_derived,
 *        transcript_coupled, hub_url, verify_url, file_url }).
 *   B) event-hub JSON - provenance may sit on the recording, its `media`,
 *      or the active version.
 *
 * `normalizeProvenance` maps EITHER shape to one view-model that the shared
 * <ContentCredentials> badge consumes, or returns `null` when there is no
 * signed credential (the badge then renders nothing - graceful degrade, same
 * pattern as `versionInfo` in views/event-hub/useEvent.js).
 */

// Content Authenticity Initiative public Verify tool. It only inspects a file the
// user drops in (remote ?source= URLs are no longer loaded), so verification is a
// two-step: download the file, then drop it into the inspector.
const VERIFY_BASE = 'https://verify.contentauthenticity.org/';

export function normalizeProvenance(rec) {
  if (!rec) return null;

  const p =
    rec.provenance ||
    (rec.media && rec.media.provenance) ||
    rec.contentCredentials ||
    null;
  if (!p) return null;

  // only surface a positive, verifiable signal
  const signed = p.signed === true || p.state === 'signed';
  if (!signed) return null;

  const fileUrl =
    p.file_url ||
    p.fileUrl ||
    (rec.media && rec.media.video && rec.media.video.url) ||
    (rec.media && rec.media.audio && rec.media.audio.url) ||
    '';

  return {
    state: 'signed',
    fileUrl,
    issuer: p.issuer || 'Decast',
    signedAt: p.signed_at || p.signedAt || null,
    algorithm: p.algorithm || null,
    manifestLabel: p.manifest_label || p.manifestLabel || null,
    unalteredSinceCapture: true,
    aiDerived: !!(p.ai_derived || p.aiDerived),
    producerDid: p.producer_did || p.producerDid || null,
    transcriptCoupled: !!(p.transcript_coupled || p.transcriptCoupled),
    hubUrl: p.hub_url || p.hubUrl || null,
    verifyUrl: VERIFY_BASE,
    // MVP signs with a self-signed cert → valid signature, issuer not yet on the
    // public C2PA trust list. `trust_pending:false` from the backend clears this.
    trustPending: p.trust_pending !== false,
  };
}

// short, readable DID for the inspector ("did:decast:AeWN…qC1x")
export function shortenDid(did) {
  if (!did) return '';
  const s = String(did);
  const tail = s.split(':').pop() || s;
  if (tail.length <= 12) return s;
  return `${s.slice(0, s.length - tail.length)}${tail.slice(0, 4)}…${tail.slice(-4)}`;
}
